import { Timestamp, WhereFilterOp } from 'firebase/firestore';
import { isEqual, isObject, transform } from 'lodash-es';

export const RESET_VALUE = -1;
export const MAX_QUERY_RESULTS = 20000;
export const NO_LIMIT_SEARCH_PARAMS = { from: 0, size: MAX_QUERY_RESULTS };

export const USERS_COLLECTION = 'stas-users';
export const USERS_PREFERENCES_COLLECTION = 'preferences';
export const USERS_LINKS_COLLECTION = 'links';
export const ORGANIZATIONS_COLLECTION = 'stas-organizations';
export const DIVISIONS_COLLECTION = 'stas-divisions';
export const SESSION_TOKENS_COLLECTION = 'stas-session-tokens';
export const DOCUMENTS_COLLECTION = 'documents';
export const PRODUCT_COLORS_COLLECTION = 'repos-product-colors';

export const NEXUS_SURVEYS_COLLECTION = 'nex-surveys';
export const HUBSPOT_DISTRIBUTORS_COLLECTION = 'hubspot-distributors';
export const HUBSPOT_OWNERS_COLLECTION = 'hubspot-owners';
export const PRODUCTION_DRAWINGS_COLLECTION = 'df-production-drawings';
export const PRODUCTION_DRAWING_ITEMS_DESCRIPTIONS_COLLECTION = 'df-drawing-items-descriptions';
export const PRODUCTION_CUTTING_ITEMS_COLLECTION = 'df-cutting-items';
export const PRODUCTION_CUTTING_ITEMS_DESCRIPTIONS_COLLECTION = 'df-cutting-items-descriptions';
export const PRODUCTION_DRAWING_STATUS_COLLECTION = 'df-drawing-status';
export const PRODUCTION_DRAWING_ITEMS_COLLECTION = 'df-drawing-items';
export const PRODUCTION_SUPPLIERS_COLLECTION = 'df-suppliers';
export const SALES_ORDER_COLLECTION = 'df-sales-orders';
export const SALES_ORDER_MODELS_COLLECTION = 'df-sales-order-models';
export const WH_PRODUCTS_COLLECTION = 'wh-products';
export const APP_CACHE_COLLECTION = 'app-cache';
export const ASSETS_COLLECTION = 'assets';
export const COLUMNS_COLLECTION = 'columns';
export const MEDIAS_COLLECTION = 'medias';
export const REPAIR_MEDIAS_COLLECTION = 'repair-medias';
export const OUTLINE_COLLECTION = 'outline';
export const LAYOUT_TEXTS_COLLECTION = 'layout-texts';
export const SYSTEMS_COLLECTION = 'systems';
export const UPRIGHTS_COLUMNS_COLLECTION = 'columns';
export const ISSUE_CFG_COLLECTION = 'peen-issue-configs';
export const ASSET_CFG_COLLECTION = 'peen-asset-types';

export const WW_WAREHOUSES_COLLECTION = 'ww-warehouses';
export const WW_WAREHOUSE_ISSUE_SEQUENCE = 'sequences/issues';
export const WW_ASSETS_COLLECTION = 'ww-assets';
export const WW_INSPECTIONS_COLLECTION = 'ww-inspections';
export const WW_ISSUES_COLLECTION = 'ww-issues';
export const WW_REPAIRS_COLLECTION = 'ww-repairs';
export const WW_SAVED_SEARCH_COLLECTION = 'ww-saved-search';
export const WW_NOTES_COLLECTION = 'ww-notes';
export const WW_IMPORTS_COLLECTION = 'ww-imports';
export const WW_REPAIR_BATCH_JOBS_COLLECTION = 'ww-repair-batch-jobs';
export const WW_INSPECTION_DELETIONS_COLLECTION = 'ww-inspection-deletions';
export const WAREHOUSE_STATE_SNAPSHOTS_COLLECTION = 'warehouse-state-snapshots';
export const WH_QUOTE_REQUESTS_COLLECTION = 'wh-quote-requests';

export const ASSET_TYPES_STORE = 'asset-types';
export const ASSETS_STORE = 'assets';
export const UPRIGHTS_STORE = 'uprights';
export const UPRIGHTS_COLUMNS_STORE = 'uprights-columns';
export const INSPECTIONS_STORE = 'inspections';
export const NOTES_STORE = 'notes';
export const ISSUE_CFGS_STORE = 'issue-configs';
export const ISSUES_STORE = 'issues';
export const REPAIRS_STORE = 'repairs';
export const SAVED_SEARCH_STORE = 'saved-search';
export const ISSUE_MEDIAS_STORE = 'issue-medias';
export const REPAIR_MEDIAS_STORE = 'repair-medias';
export const OUTLINES_STORE = 'outlines';
export const TEXTS_STORE = 'texts';
export const OFFLINE_WAREHOUSES_STORE = 'offline-warehouses';
export const SYSTEM2DS_STORE = 'system2ds';
export const WAREHOUSES_STORE = 'warehouses';

export const NOTIFICATION_COLLECTION = 'av-notifications';

export const ALL_WAREHOUSE_STORES = [
  ASSETS_STORE,
  ISSUES_STORE,
  INSPECTIONS_STORE,
  ISSUE_MEDIAS_STORE,
  NOTES_STORE,
  SYSTEM2DS_STORE,
  UPRIGHTS_STORE,
  OUTLINES_STORE,
  TEXTS_STORE,
  UPRIGHTS_COLUMNS_STORE,
  SAVED_SEARCH_STORE,
];

export type ValueOf<T> = T[keyof T];
export type Scalar = string | number | boolean;

export interface Audit {
  createdBy?: string;
  createdDate?: Timestamp | Date;
  lastModifiedBy?: string;
  lastModifiedDate?: Timestamp | Date;
}

export interface Auditable {
  audit?: Audit;
}

export interface SoftDeletable {
  deleted?: boolean;
}

export interface SearchResultPage {
  number?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;

  from?: number;
  to?: number;
  page?: number;
}

export interface SearchResult<T> {
  page?: SearchResultPage;
  result?: T[];
}

export interface Query {
  from?: number;
  size?: number;
  filters?: Record<string, Scalar | Scalar[]>;
  filtersOps?: { field: string; op: WhereFilterOp; value: any }[];
  filterFn?: (o: unknown) => boolean;
  searchPhrase?: string;
  start?: string;
  end?: string;
  sort?: Record<string, 'asc' | 'desc'>;
}

export interface DivisionQuery extends Query {
  filters: { 'division._id': string; [key: string]: string | number };
}

export interface Dto<T = string | number> extends Auditable {
  _id?: T;
}

export interface Dictionary<T> {
  [key: string]: T;
}

export interface IDName<T = string> {
  _id?: T;
  name?: string;
}

export declare interface KeyValue<K, V> {
  key: K;
  value: V;
}

export interface ContactInfo {
  addresses?: string[];
  phones?: string[];
  fax?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

export interface Contactable extends Dto<string> {
  contactInfo?: ContactInfo;
}

/**
 * Deep diff between two object, using lodash
 */
export const difference = (object: any, base: any, excludedProps: string[] = []) => {
  if (!object && base) {
    return base;
  } else if (!base && object) {
    return object;
  }

  const diffs = transform(object, (result: any, value: any, key: string) => {
    if (!excludedProps.includes(key) && !isEqual(value, base[key])) {
      if (isObject(value) && isObject(base[key])) {
        const propDiff = difference(value, base[key], excludedProps);
        if (propDiff) {
          result[key] = propDiff;
        }
      } else {
        result[key] = value;
      }
    }
  });

  const keys = Object.keys(diffs);
  return keys.length > 0 ? diffs : undefined;
};

export const isDefined = <T>(o: T) => !!o;
